'use client';

import {
  Stepper as MuiStepper,
  Step,
  StepLabel,
  MobileStepper,
} from '@mui/material';
import { ReactNode } from 'react';

import { useIsMobile } from '@web/hooks';

import { MainButton } from '../../atomic-atoms/buttons/MainButton';

import styles from './Stepper.module.scss';

export type StepItem = {
  label: string;
  optional?: ReactNode;
  disabled?: boolean;
  content?: ReactNode;
};

export type StepperVariant = 'horizontal' | 'vertical' | 'mobile' | 'auto';

export type StepperProps = {
  steps: StepItem[];
  variant?: StepperVariant;
  activeStep: number;
  onStepChange?: (step: number) => void;
  linear?: boolean;
  className?: string;
  // * Finish button.
  showFinish?: boolean;
  onFinish?: () => void;
  mobileProgressProps?: {
    nextLabel?: string;
    backLabel?: string;
    showDots?: boolean;
  };
};

export function Stepper({
  steps,
  variant = 'auto',
  activeStep,
  onStepChange,
  linear = true,
  showFinish = false,
  onFinish,
  mobileProgressProps = {
    showDots: true,
  },
  className,
}: StepperProps) {
  const isMobile = useIsMobile();
  const resolvedVariant: Exclude<StepperVariant, 'auto'> =
    variant === 'auto' ? (isMobile ? 'mobile' : 'horizontal') : variant;

  if (resolvedVariant === 'mobile') {
    const { nextLabel = 'Next', backLabel = 'Back' } =
      mobileProgressProps || {};

    const showMobileButtons = !linear || showFinish;

    const currentStep = Math.min(activeStep + 1, steps.length);

    return (
      <>
        <div className={styles.mobileHeader}>
          <div className={styles.mobileStepInfo}>
            Step {currentStep} of {steps.length}
          </div>

          <div className={styles.mobileLabel}>{steps[activeStep]?.label}</div>

          {steps[activeStep]?.optional && (
            <div className={styles.mobileOptional}>
              {steps[activeStep]?.optional}
            </div>
          )}
        </div>

        <MobileStepper
          variant={mobileProgressProps?.showDots ? 'dots' : 'progress'}
          steps={steps.length}
          activeStep={activeStep}
          position="static"
          nextButton={
            showMobileButtons && (
              <MainButton
                onClick={() => onStepChange?.(activeStep + 1)}
                disabled={activeStep === steps.length - 1}
              >
                {nextLabel}
              </MainButton>
            )
          }
          backButton={
            showMobileButtons && (
              <MainButton
                onClick={() => onStepChange?.(activeStep - 1)}
                disabled={activeStep === 0}
              >
                {backLabel}
              </MainButton>
            )
          }
          className={className}
        />

        <div className={styles.stepperContent}>
          {steps[activeStep]?.content}

          {showFinish && activeStep === steps.length - 1 && (
            <MainButton onClick={onFinish}>Finish</MainButton>
          )}
        </div>
      </>
    );
  }

  // * DESKTOP (Horizontal + Vertical).
  return (
    <>
      <MuiStepper
        activeStep={activeStep}
        orientation={resolvedVariant === 'vertical' ? 'vertical' : 'horizontal'}
        nonLinear={!linear}
        className={className}
      >
        {steps.map((step, index) => (
          <Step
            key={step.label}
            disabled={step.disabled}
            onClick={!linear ? () => onStepChange?.(index) : undefined}
          >
            <StepLabel optional={step.optional}>{step.label}</StepLabel>
          </Step>
        ))}
      </MuiStepper>

      <div className={styles.stepperContent}>
        {steps[activeStep]?.content}

        {showFinish && activeStep === steps.length - 1 && (
          <MainButton onClick={onFinish}>Finish</MainButton>
        )}
      </div>
    </>
  );
}

// * IMPLEMENTATION EXAMPLES.

{
  /* <Stepper
      steps={[
        {
          label: 'Login',
          content: <div>Form</div>,
        },
        {
          label: 'Details',
          content: <div>Details</div>,
        },
        {
          label: 'Review',
          content: <div>Review</div>,
        },
      ]}
      activeStep={activeStep}
      onStepChange={setActiveStep}
      showFinish
      onFinish={() => console.log('Done!')}
/> */
}
